import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { AccountInfo } from "@azure/msal-browser";
import { useAuth } from "@/composables/useAuth";
import api from "@/services/api";

export const useAuthStore = defineStore("auth", () => {
    const account = ref<AccountInfo | null>(null);
    const ready = ref(false);
    const loading = ref(false);
    const appUser = ref<any | null>(null);
    const sessionMessage = ref("");

    const isAuthenticated = computed(() => !!account.value);
    const userName = computed(() => account.value?.name || "");
    const userEmail = computed(() => account.value?.username || "");
    const userAvatar = computed(() => appUser.value?.avatar || null);

    const {
        initializeMsal,
        refreshUser,
        login,
        logout,
        getAccessToken,
        user,
        interactionInProgress,
    } = useAuth();

    async function bootstrap() {
        if (ready.value) return;

        loading.value = true;

        try {
            await initializeMsal();
            await refreshUser();

            account.value = user.value;

            if (account.value && !appUser.value) {
                try {
                    await fetchMe();
                } catch (error) {
                    console.warn("fetchMe during bootstrap did not complete", error);
                    return;
                }
            }
        } finally {
            ready.value = true;
            loading.value = false;
        }
    }

    async function signIn(returnPath?: string) {
        await login(returnPath);
    }

    async function signOut() {
        await logout();
        account.value = null;
        appUser.value = null;
        ready.value = false;
        sessionMessage.value = "";
    }

    async function syncUser() {
        await refreshUser();
        account.value = user.value;
        ready.value = true;
        loading.value = false;
    }

    async function fetchAccessToken(forceRefresh = false) {
        return await getAccessToken(forceRefresh);
    }

    async function fetchMe() {
        try {
            const response = await api.get("/me");
            // appUser.value = response.data.app_user;
            appUser.value = response.data.user;
            return response.data;
        } catch (error) {
            appUser.value = null;
            throw error;
        }
    }

    function setSessionMessage(message: string) {
        sessionMessage.value = message;
    }

    function clearSessionMessage() {
        sessionMessage.value = "";
    }

    return {
        account,
        appUser,
        ready,
        loading,
        isAuthenticated,
        userName,
        userEmail,
        userAvatar,
        interactionInProgress,
        sessionMessage,
        bootstrap,
        signIn,
        signOut,
        syncUser,
        fetchAccessToken,
        fetchMe,
        setSessionMessage,
        clearSessionMessage,
    };
});